import { c as _c } from "react/compiler-runtime";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { getSeatShortage } from "@components/table/members/bulk-actions/bulk-seats-helper";
import { isCompanyMemberDetails } from "@custom-types/member-type-guards";
import { Alert } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useFetchWorkspaceMembers } from "@hooks/use-role-xg-seat";
import { useXgSeatRequirement } from "@hooks/use-xg-seat-requirement";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { areCompanyMembersLoadingSelector, companyMembersByKeySelector, fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { COMPANY_SUBSCRIPTIONS_QUERY_OPTIONS } from "@src/constants/subscription-constants";
import { activeSeatsAvailableSelector, fetchingSdbCompanyFlagsSelector, hasFetchedCompanySubscriptionsSelector } from "@store/sdb-company/sdb-company-selector";
import { fetchCompanySubscriptions } from "@store/sdb-company/sdb-company-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updatingItemsSelector } from "@store/table/table-selector";
import { formatUserRoleType, nounPluralize } from "@utils/data-display";
import { findWorkspaceMember, isMissingXgSeat } from "@utils/invite-xg-seat-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect, useRef } from "react";

/** Why a member is left out when the user cannot give them the seat their new role needs */
export const XG_SEAT_NOT_ASSIGNABLE_REASON = "Needs a Sphere XG seat, which you cannot assign";

/** Why a member is left out when it is not known whether they hold a seat */
export const XG_SEATS_NOT_LOADED_REASON = "The Sphere XG seats could not be loaded";

/** Why the change cannot be confirmed yet when it is not known how many seats are free */
export const FREE_XG_SEATS_UNKNOWN_MESSAGE = "The free Sphere XG seats are not known yet. Please wait a moment, or reload the page.";
/**
 * Applies the Sphere XG seats to changing the role of several members at once, like
 * useRoleXgSeat does for one. On a Scan plan, the members that lack the seat the new role needs are
 * given one first, which only a user that may assign seats can do, and only as long as there are
 * enough free seats for all of them.
 *
 * Teams cannot hold a seat, so they are never given one here. Whether a team may take on a role that
 * needs a seat is up to the caller, e.g. by not offering that role to teams at all.
 */
export function useBulkRoleXgSeats({
  doesRoleNeedXgSeat,
  newRole,
  selectedEntities,
  isEnabled = true
}) {
  const {
    canManageXgSeats,
    canViewSubscriptionDetails
  } = useHasUserValidRoleCompanyLevel();
  const {
    setXgSeatPermission,
    refreshSeatUsage
  } = useSceneSeatPermission();
  const xgSeatRequirement = useXgSeatRequirement();
  const membersByKey = useAppSelector(companyMembersByKeySelector);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    hasFetchingCompanyMembersFailed
  } = useAppSelector(fetchingMembersFlagsSelector);
  const updatingItems = useAppSelector(updatingItemsSelector);
  const storedFreeSeats = useAppSelector(activeSeatsAvailableSelector);
  const hasFetchedSubscriptions = useAppSelector(hasFetchedCompanySubscriptionsSelector);
  // Undefined while the subscriptions are not known, e.g. while they load. A failed fetch keeps
  // those from before in the store, whose count of free seats may be outdated. Once they are
  // known, a plan without assigned seats has none free, as the dialog for a single member counts.
  const freeSeats = hasFetchedSubscriptions ? storedFreeSeats ?? 0 : undefined;

  // Whether any seat was given, so that the seats in use are only fetched again when they changed
  const hasGivenSeatRef = useRef(false);

  // While the plan is not known, the seats are checked as if it required them. No free seats are
  // known then either, so no member is given a seat until the plan is known.
  const isChecking = isEnabled && xgSeatRequirement !== "notRequired";
  const hasMembersToCheck = isChecking && selectedEntities.some(member => !FaroCoreApiUtils.Member.isTeam(member));
  // Group and project members do not list their seats, which only the workspace members do
  useFetchWorkspaceMembers(hasMembersToCheck);
  // Neither the plan nor the free seats are known without the subscriptions, which also decide the
  // roles a table of teams is offered, so this is tried even when the seats do not apply
  useRetryUnknownSubscriptions(canViewSubscriptionDetails && !hasFetchedSubscriptions);
  const notAllowedIds = new Set(updatingItems.filter(item => item.status === "not-allowed").map(item_0 => item_0.id));
  // Only the members that the change applies to are given a seat
  const membersToSeat = selectedEntities.filter(member_0 => !notAllowedIds.has(member_0.identity) && isXgSeatMissing(member_0, newRole));
  // Seats are only given once it is known that there are enough free ones, e.g. not while the
  // subscriptions load or after they failed to
  const areFreeSeatsUnknown = membersToSeat.length > 0 && freeSeats === undefined;
  const seatShortage = freeSeats === undefined ? undefined : getSeatShortage({
    numberOfChangingMembers: membersToSeat.length,
    seatsAvailable: freeSeats,
    seat: "xgSeat",
    role: newRole
  });
  return {
    getSkipReason,
    isRoleDisabled,
    xgSeatAlert: getXgSeatAlert(),
    shouldDisableConfirm: !!seatShortage || areFreeSeatsUnknown,
    changeRoleWithXgSeat,
    refreshSeatUsage: refreshSeatUsageIfChanged
  };

  /** Whether the member is not a team and lacks the Sphere XG seat that the role needs */
  function isXgSeatMissing(member_1, role) {
    if (!isChecking || FaroCoreApiUtils.Member.isTeam(member_1) || role === getAssignedRole(member_1) || !doesRoleNeedXgSeat(role)) {
      return false;
    }
    const workspaceMember = findWorkspaceMember(membersByKey, member_1);
    return !workspaceMember || isMissingXgSeat(workspaceMember);
  }
  function getSkipReason(member_2, role_0) {
    if (!isXgSeatMissing(member_2, role_0)) {
      return undefined;
    }
    if (hasFetchingCompanyMembersFailed && !findWorkspaceMember(membersByKey, member_2)) {
      return XG_SEATS_NOT_LOADED_REASON;
    }
    return canManageXgSeats ? undefined : XG_SEAT_NOT_ASSIGNABLE_REASON;
  }
  function isRoleDisabled(role_1) {
    return hasMembersToCheck && areMembersLoading && doesRoleNeedXgSeat(role_1);
  }

  /** Returns the alert about the seats given along with the role, or null when none are */
  function getXgSeatAlert() {
    if (seatShortage) {
      return <Alert title={seatShortage} variant="error" sx={{
        mb: SPACE_ELEMENTS_OF_MODAL
      }} />;
    }
    if (areFreeSeatsUnknown) {
      return <Alert title={FREE_XG_SEATS_UNKNOWN_MESSAGE} variant="warning" sx={{
        mb: SPACE_ELEMENTS_OF_MODAL
      }} />;
    }
    if (!membersToSeat.length) {
      return null;
    }
    const numberOfMembers = membersToSeat.length;
    const members = nounPluralize({
      counter: numberOfMembers,
      word: "member",
      shouldShowCounter: false
    });
    const verb = numberOfMembers === 1 ? "is" : "are";
    return <Alert title={<span>
            <var>{numberOfMembers}</var> {members} {verb} given a Sphere XG
            seat, which they need to be {formatUserRoleType(newRole)}.
          </span>} variant="info" sx={{
      mb: SPACE_ELEMENTS_OF_MODAL
    }} />;
  }
  async function changeRoleWithXgSeat(member_3, changeRole) {
    if (isXgSeatMissing(member_3, newRole)) {
      try {
        await setXgSeatPermission(findWorkspaceMember(membersByKey, member_3) ?? member_3);
      } catch (error) {
        // Listed with the member in the dialog rather than shown in an error toast that would log it
        sentryCaptureError({
          error,
          title: "Cannot give a Sphere XG seat"
        });
        throw error;
      }
      hasGivenSeatRef.current = true;
    }
    await changeRole();
  }
  function refreshSeatUsageIfChanged() {
    if (hasGivenSeatRef.current) {
      hasGivenSeatRef.current = false;
      refreshSeatUsage();
    }
  }
}

/**
 * Fetches the subscriptions once more while they are not known, e.g. after fetching them failed, so
 * that the seat roles are not blocked until the page is reloaded. It only tries once until they are
 * known again, so that a request that keeps failing is not sent again and again.
 *
 * @param isUnknown Whether the subscriptions are needed and not known
 */
function useRetryUnknownSubscriptions(isUnknown) {
  const $ = _c(5);
  const dispatch = useAppDispatch();
  const {
    isFetchingCompanySubscriptions
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const hasRetriedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== isFetchingCompanySubscriptions || $[2] !== isUnknown) {
    t0 = () => {
      if (!isUnknown) {
        hasRetriedRef.current = false;
        return;
      }
      if (!isFetchingCompanySubscriptions && !hasRetriedRef.current) {
        hasRetriedRef.current = true;
        dispatch(fetchCompanySubscriptions({
          queryOptions: COMPANY_SUBSCRIPTIONS_QUERY_OPTIONS
        }));
      }
    };
    t1 = [dispatch, isFetchingCompanySubscriptions, isUnknown];
    $[0] = dispatch;
    $[1] = isFetchingCompanySubscriptions;
    $[2] = isUnknown;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}

/**
 * Returns the role assigned to the member directly. A workspace member lists their effective role,
 * which may come from a team or a group, so that a member that is admin through a team still needs
 * the seat to be made admin themselves.
 */
function getAssignedRole(member) {
  return isCompanyMemberDetails(member) ? member.userRole : member.role;
}